// Admin → Activity. V3.10.2: Notifications (admins) fill the top half, Activity the bottom half; each part scrolls on its own.
// V3.10.1: two Activity views —
//   User activity (who is online now + usage history/stats; Skyward admins with Support view permission only)
//   System activity (every change made in the admin area, from audit_log; all staff).
// The view is kept in the address (?view=users | system) so links and the old /admin/online keep working.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { adminApi, type AuditRow, type Org } from "../../lib/admin";
import { fmtDateTime } from "./ui";
import UserActivity from "./Online";
import Notifications from "./Notifications";

const PAGE = 100;
type View = "users" | "system";

export default function Activity({ orgs, canOnline, meLoaded, isAdmin, userId }: { orgs: Org[]; canOnline: boolean; meLoaded: boolean; isAdmin: boolean; userId: string }) {
  const [params, setParams] = useSearchParams();
  const asked = params.get("view") as View | null;
  const view: View = canOnline ? (asked === "system" ? "system" : "users") : "system";
  const setView = (v: View) => setParams((p) => { const n = new URLSearchParams(p); n.set("view", v); return n; }, { replace: true });
  const online = useOnlineCount(canOnline && view === "system");

  return (
    <section className={`activity ${isAdmin ? "split" : ""}`}>
      {isAdmin && <div className="act-pane act-notifs"><Notifications userId={userId} embedded /></div>}
      <div className="act-pane act-log">
        <div className="act-head">
          <h2>Activity</h2>
          {canOnline && (
            <div className="act-switch" role="tablist" aria-label="Activity view">
              <button role="tab" aria-selected={view === "users"} className={view === "users" ? "on" : ""} onClick={() => setView("users")}>
                User activity{online != null && view === "system" && <span className="act-live" title="Client users online now">{online}</span>}
              </button>
              <button role="tab" aria-selected={view === "system"} className={view === "system" ? "on" : ""} onClick={() => setView("system")}>System activity</button>
            </div>
          )}
          <span className="sub">{view === "users" ? "Who is using the platform now, and how it's used over time." : "Every change made in the admin area is recorded here."}</span>
        </div>
        <div className="act-body">
          {!meLoaded ? <p className="sub">Loading…</p> : view === "users" ? <UserActivity orgs={orgs} /> : <SystemActivity orgs={orgs} />}
        </div>
      </div>
    </section>
  );
}

/** Small "n online" count for the User activity button while System activity is showing (every 60 s). */
function useOnlineCount(enabled: boolean) {
  const [n, setN] = useState<number | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const load = () => adminApi<{ counts?: { online?: number } }>("GET", "online")
      .then((d) => { if (live && typeof d?.counts?.online === "number") setN(d.counts.online); }, () => undefined);
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 60_000);
    return () => { live = false; clearInterval(t); };
  }, [enabled]);
  return enabled ? n : null;
}

function SystemActivity({ orgs }: { orgs: Org[] }) {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [entity, setEntity] = useState("");
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => {
    let qy = supabase.from("audit_log").select("id, at, actor_email, action, entity, entity_id, org_id, summary")
      .order("at", { ascending: false }).limit(limit);
    if (entity) qy = qy.eq("entity", entity);
    qy.then(({ data }) => setRows((data ?? []) as AuditRow[]));
  }, [entity, limit]);

  const orgName = (id: string | null) => (id ? orgs.find((o) => o.id === id)?.name ?? "" : "");

  return (
    <>
      <div className="online-bar">
        <select className="asearch" value={entity} onChange={(e) => setEntity(e.target.value)} aria-label="Show">
          <option value="">Everything</option><option value="organisation">Companies</option><option value="user">Users</option>
          <option value="device">Devices</option><option value="sim">SIM cards</option>
        </select>
      </div>
      <div className="atable-wrap">
        <table className="atable static">
          <thead><tr><th>When (Fiji)</th><th>Who</th><th>What</th><th>Company</th></tr></thead>
          <tbody>
            {rows === null && <tr className="empty"><td colSpan={4}>Loading…</td></tr>}
            {rows?.map((r) => (
              <tr key={r.id}>
                <td className="mono">{fmtDateTime(r.at)}</td>
                <td>{r.actor_email ?? "system"}</td>
                <td>{r.summary ?? r.action}<div className="sub mono">{r.action}</div></td>
                <td>{orgName(r.org_id)}</td>
              </tr>
            ))}
            {rows && !rows.length && <tr className="empty"><td colSpan={4}>Nothing recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {rows && rows.length === limit && <p className="pad"><button className="btn-ghost" onClick={() => setLimit(limit + PAGE)}>Load more</button></p>}
    </>
  );
}
